/**
 * Helpers for the per-member task-assignment studio.
 *
 * Two roles drive this view:
 *   • Department Head — re-assigns tasks in their department to one of their managers.
 *   • Manager         — re-assigns tasks delegated to them to one of their engineers
 *                        (or keeps the task themselves).
 */

import type {
  EpcProjectMembershipRow,
  EpcProjectTeamRole,
} from '../../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { taskAssignedUserId, taskOrgUnitId } from './epcScheduleDepartmentAssignment';

/* ─────────── role / capability ─────────── */

export type MemberAssignRole = 'department_head' | 'manager';

/** Resolve the actor's role for the member-assign studio.
 *  Returns null when the current user has nothing to delegate (governance / engineer / outsider). */
export function resolveMemberAssignRole(
  mine: EpcProjectMembershipRow | undefined,
): MemberAssignRole | null {
  if (!mine) return null;
  if (mine.role === 'department_head') return 'department_head';
  if (mine.role === 'manager') return 'manager';
  return null;
}

/* ─────────── task scoping ─────────── */

/**
 * Tasks within the actor's authority — i.e. the task pool they may delegate from.
 *  • department_head: every task in their department.
 *  • manager:         tasks where the assignee is them OR (in their dept and unassigned).
 *
 * Index list is preserved so the WBS tree can render parents/summaries in their original positions.
 */
export function authoritativeTaskIndices(
  tasks: EpcScheduleTaskRow[],
  role: MemberAssignRole,
  mine: EpcProjectMembershipRow,
  myUserId: string,
): number[] {
  const out: number[] = [];
  for (let i = 0; i < tasks.length; i += 1) {
    const t = tasks[i]!;
    if (role === 'department_head') {
      if (mine.orgUnitId && taskOrgUnitId(t) === mine.orgUnitId) out.push(i);
      continue;
    }
    if (role === 'manager') {
      const a = taskAssignedUserId(t);
      if (a === myUserId) {
        out.push(i);
        continue;
      }
      if (!a && mine.orgUnitId && taskOrgUnitId(t) === mine.orgUnitId) {
        out.push(i);
      }
    }
  }
  return out;
}

/* ─────────── eligible assignees ─────────── */

/** Members the actor may delegate to (excluding the actor themselves). */
export function eligibleAssignees(
  role: MemberAssignRole,
  mine: EpcProjectMembershipRow,
  myUserId: string,
  memberships: EpcProjectMembershipRow[],
): EpcProjectMembershipRow[] {
  const ofRole = (r: EpcProjectTeamRole, target: EpcProjectMembershipRow) => target.role === r;
  if (role === 'department_head') {
    if (!mine.orgUnitId) return [];
    return memberships.filter(
      (m) =>
        !m.inactive && ofRole('manager', m) && m.orgUnitId === mine.orgUnitId,
    );
  }
  /* manager → engineers reporting to me */
  return memberships.filter(
    (m) =>
      !m.inactive &&
      ofRole('site_engineer', m) &&
      m.reportsToUserId === myUserId,
  );
}

/* ─────────── mutation ─────────── */

/** Set `assignedUserId` for a set of row indices. Pass `undefined` to clear. */
export function applyAssigneeToTaskIndices(
  tasks: EpcScheduleTaskRow[],
  indices: Iterable<number>,
  userId: string | undefined,
): EpcScheduleTaskRow[] {
  const set = new Set(indices);
  return tasks.map((t, i) => {
    if (!set.has(i)) return t;
    if (!userId) return { ...t, assignedUserId: undefined };
    return { ...t, assignedUserId: userId };
  });
}

/* ─────────── workload counters ─────────── */

export type WorkloadEntry = {
  userId: string;
  taskCount: number;
};

/** Count how many in-scope tasks each eligible assignee has. */
export function computeWorkload(
  tasks: EpcScheduleTaskRow[],
  scopeIndices: number[],
  assignees: EpcProjectMembershipRow[],
): Map<string, WorkloadEntry> {
  const m = new Map<string, WorkloadEntry>();
  for (const a of assignees) m.set(a.userId, { userId: a.userId, taskCount: 0 });
  for (const i of scopeIndices) {
    const t = tasks[i];
    if (!t) continue;
    const a = taskAssignedUserId(t);
    if (!a) continue;
    const e = m.get(a);
    if (e) e.taskCount += 1;
  }
  return m;
}

/* ─────────── stats ─────────── */

export type AssignStats = {
  total: number;
  assigned: number;
  unassigned: number;
  /** Subset of `total` that is a leaf (i.e. an actual work-package, not a summary row). */
  leafTotal: number;
  leafAssigned: number;
  leafUnassigned: number;
};

export function computeStats(
  tasks: EpcScheduleTaskRow[],
  scopeIndices: number[],
  isLeaf: (i: number) => boolean,
): AssignStats {
  let assigned = 0;
  let leafTotal = 0;
  let leafAssigned = 0;
  for (const i of scopeIndices) {
    const t = tasks[i];
    if (!t) continue;
    const has = !!taskAssignedUserId(t);
    if (has) assigned += 1;
    if (isLeaf(i)) {
      leafTotal += 1;
      if (has) leafAssigned += 1;
    }
  }
  const total = scopeIndices.length;
  return {
    total,
    assigned,
    unassigned: total - assigned,
    leafTotal,
    leafAssigned,
    leafUnassigned: leafTotal - leafAssigned,
  };
}
